import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocalStorage } from 'react-use'
import { useQueryClient } from '@tanstack/react-query'
import { v4 as uuid4 } from 'uuid'

import { AppBuilderAPI } from '@makyoapp/makyo-sdk'
import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { IUpdateProjectPrivacyBody, ProjectInfosAPI } from '@makyoapp/makyo-sdk/api/Projects'
import { IBody, PageType } from '@makyoapp/makyo-sdk/models/AppBuilder'
import {
  ActionFlag,
  BuilderDialog,
  BuilderDroppableElement,
  BuilderMenu,
  BuilderPage,
  DialogAction,
  fromSdkApp,
  GalleryModal,
  IActionRepeaterValue,
  IActionType,
  MenuAction,
  PageAction,
  ProjectAdapter,
} from '@makyoapp/makyo-common'
import { ColorVariant, isColorVariant } from '@makyoapp/makyo-tw-ui'

import { formatComponents } from '@/core/ai/app-builder/components/format-components'
import { formatAiDialog } from '@/core/ai/app-builder/dialogs'
import { formatAiMenu } from '@/core/ai/app-builder/menus'
import { formatAiOverlay } from '@/core/ai/app-builder/overlays'
import { formatAiPage } from '@/core/ai/app-builder/pages'
import { IAppClient } from '@/core/app-builder/app/app'
import { IViewComponent } from '@/core/app-builder/app/component'
import { IAppLayer } from '@/core/app-builder/app/layer'
import { SubscriberUpdateType } from '@/core/app-builder/app/types'
import constate from '@/lib/constate'
import { queryKeys } from '@/lib/react-query'
import { fromSdkDialogList, toSdkDialog } from '@/adapters/__project__/BuilderDialogAdapter'
import { fromSdkMenuList, toSdkMenu } from '@/adapters/__project__/BuilderMenuAdapter'
import {
  BuilderNavbarSettings,
  fromSdkNavbarSettings,
  NavbarSettings,
  toSdkNavbarSettings,
} from '@/adapters/__project__/BuilderNavbarAdapter'
import { fromSdkOverlayList } from '@/adapters/__project__/BuilderOverlayAdapter'
import { fromSdkPage, fromSdkPageBody, fromSdkPageList } from '@/adapters/__project__/BuilderPageAdapter'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppContext } from '@/context/AppContext'
import { useReqError } from '@/hooks/useReqError'
import { CustomFontsRecord, DEFAULT_COMPONENTS_GROUPS, TreeItems } from '@/shared/builder'
import { ComponentPanelTab, LeadingSidebarTab } from '@/shared/builder-ui'
import { EVENT_KEYS } from '@/shared/custom-event'
import { DEFAULT_NAV_SETTINGS } from '@/shared/main-navigation-bar'
import { projectPlanColor } from '@/shared/misc'
import { BuilderOverlay } from '@/shared/overlay'
import { genIdent, isEmpty } from '@/shared/utils'
import { createElementRaw } from '@/utils/builder/extra-parsers'

interface ListCheckState {
  id: string
  items: string[]
  icon: string
  action?: IActionRepeaterValue
  extraActionProps: Record<string, unknown>
}

interface ActiveLayerState {
  type: 'page' | 'overlay'
  id: string
  index: number
  tableId?: string
}

interface ActiveComponentState {
  layerType: 'page' | 'overlay'
  compId: string
  id: string
  groupId: string
}

interface AppBuilderProviderProps {
  appClient: IAppClient
}

interface IUpdateProjectShowHideInfosBody {
  _WhiteLabeling?: boolean
  _InstallationModal?: boolean
  _CookiesNotification?: boolean
}

/**
 *
 * @todo
 * - [] create a login form component inside login page by default
 */
const [AppBuilderProvider, useAppBuilderContext] = constate(
  'AppBuilderContext',
  ({ appClient }: AppBuilderProviderProps) => {
    // access param
    const appID = appClient.appID || ''

    // Access Global Contexts
    const { version, setVersion } = useAppContext()
    const { tables, userTable } = useDbClientContext()
    const printAPIError = useReqError()
    const queryClient = useQueryClient()

    const appUserTableID = window.appUserTableID
    const appUserTable = tables.find((tb) => tb.id === appUserTableID)

    // Core States
    const [appBuilderLoaded, setAppBuilderLoaded] = useState(false)
    const [builderGlobals, setBuilderGlobals] = useState({
      elements: [] as BuilderDroppableElement[],
      settings: {} as Record<string, any>,
    })
    const [project, setProject] = useState<ProjectAdapter | undefined>(undefined)
    const [pages, setPages] = useState<BuilderPage[]>([])
    const [overlays, setOverlays] = useState<BuilderOverlay[]>([])
    const [activeOverlays, setActiveOverlays] = useState<BuilderOverlay[]>([])
    const [navbarSettings, setNavbarSettings] = useState<BuilderNavbarSettings>(DEFAULT_NAV_SETTINGS)
    const [menus, setMenus] = useState<BuilderMenu[]>([])
    const [dialogs, setDialogs] = useState<BuilderDialog[]>([])
    const [toolbar, setToolbar] = useState<{ show: (location: Location) => boolean; component: React.ReactNode }[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isDevelopmentMode, setIsDevelopmentMode] = useState(false)
    const [activeComponentAccordion, setActiveComponentAccordion] = useState<string | undefined>(undefined)
    const [activeComponentTab, setActiveComponentTab] = useState<ComponentPanelTab>(ComponentPanelTab.Data)
    const [showBuiltInBranding, setShowBuiltInBranding] = useState(true)
    const [showInstallationModal, setShowInstallationModal] = useState(true)
    const [showCookieConfirmation, setShowCookieConfirmation] = useState(true)

    const userProfilePage = useMemo(() => {
      return pages.find((p) => p.type === PageType.UserProfile)
    }, [pages])

    // Project Specific
    const [statusBarColor, setStatusBarColor] = useState<{ dark: ColorVariant; light: ColorVariant }>({
      dark: (project?.statusBarColorDark as ColorVariant) || 'neutral-0',
      light: (project?.statusBarColor as ColorVariant) || 'neutral-0',
    })
    const [topBarColor, setTopBarColor] = useState<{ dark: ColorVariant; light: ColorVariant }>({
      dark: (project?.topBarColorDark as ColorVariant) || 'primary-80',
      light: (project?.topBarColor as ColorVariant) || 'primary-80',
    })
    const [topBarShowIcon, setTopBarShowIcon] = useState<boolean>(project?.topBarShowIcon || true)
    const [topBarVisibility, setTopBarVisibility] = useState<boolean>(project?.topBarVisibility || true)
    const [topBarShowTitle, setTopBarShowTitle] = useState<boolean>(project?.topBarShowTitle || true)
    const [showLanguageSwitcher, setShowLanguageSwitcher] = useState<boolean>(false)
    const [showThemeSwitcher, setShowThemeSwitcher] = useState<boolean>(false)

    // Misc
    const [showFabWarningModal, setShowFabWarningModal] = useState(false)
    const [fabWarningReason, setFabWarningReason] = useState<'limit' | 'header'>('limit')
    const [showDateTimeValidationModal, setShowDateTimeValidationModal] = useState(false)

    // Clipboard
    const [clipboard, setClipboard] = useState<{ pageId: string; elId: string } | null>(null)
    const [clipboardAll, setClipboardAll] = useState<{ pageId: string; elements: TreeItems } | null>(null)

    // ============================== Publish ==============================
    const updateVersion = useCallback(async () => {
      try {
        await queryClient.invalidateQueries({ queryKey: [queryKeys.appVersionHistory, appID] }) // see ToolbarV2.tsx for more details
        queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appID] })
      } catch (e) {
        printAPIError(e as ErrorResponse)
      }
    }, [appID, printAPIError, queryClient])

    // ============================== Load Data ==============================

    const handleLoadData = useCallback((appClient: IAppClient) => {
      const builderVersion = appClient.builderSettings?._Version
      // set initial version from builder
      if (!!builderVersion) {
        setVersion(builderVersion._Number)
      }

      // set project
      if (appClient.projectSettings) {
        setTopBarVisibility(appClient.projectSettings._Appearance._NavBarVisibility)
        setTopBarShowIcon(appClient.projectSettings._Appearance._NavBarIconVisibility)
        setTopBarShowTitle(appClient.projectSettings._Appearance._NavBarTitleVisibility)
        let projectAdapter = fromSdkApp(appClient.projectSettings, projectPlanColor)
        setProject(projectAdapter)
        if (projectAdapter.statusBarColor) {
          setStatusBarColor({
            dark: isColorVariant(projectAdapter.statusBarColorDark) ? projectAdapter.statusBarColorDark : 'neutral-0',
            light: isColorVariant(projectAdapter.statusBarColor) ? projectAdapter.statusBarColor : 'neutral-0',
          })
        }
        setTopBarColor({
          light: isColorVariant(projectAdapter.topBarColor) ? projectAdapter.topBarColor : 'primary-80',
          dark: isColorVariant(projectAdapter.topBarColorDark) ? projectAdapter.topBarColorDark : 'primary-80',
        })
        setShowBuiltInBranding(appClient.projectSettings?._WhiteLabeling || false)
        setShowInstallationModal(appClient.projectSettings?._InstallationModal || false)
        setShowCookieConfirmation(appClient.projectSettings?._CookiesNotification || false)
        setShowLanguageSwitcher(appClient.projectSettings?._Appearance._LanguageSelectorVisibility || false)
        setShowThemeSwitcher(appClient.projectSettings?._Appearance._ThemeSelectorVisibility || false)
      }

      // set builder globals, navbar, menus & dialogs
      if (appClient.builderSettings) {
        if (appClient.builderSettings._Globals) {
          setBuilderGlobals({
            elements: fromSdkPageBody(appClient.builderSettings._Globals._Body, DEFAULT_COMPONENTS_GROUPS),
            settings: appClient.builderSettings._Globals._BodySettings || {},
          })
        }

        if (!isEmpty(appClient.builderSettings._NavBar)) {
          setNavbarSettings(fromSdkNavbarSettings(appClient.builderSettings._NavBar))
        }
        setMenus(fromSdkMenuList(appClient.builderSettings._Menus ?? []))
        setDialogs(fromSdkDialogList(appClient.builderSettings._Dialogs ?? []))
      }

      // set pages
      if (appClient.pagesMap?.size) {
        const pageList = Array.from(appClient.pagesMap.values()).map((page) => page.settings)
        const adaptedPages = fromSdkPageList(pageList, DEFAULT_COMPONENTS_GROUPS)
        setPages(adaptedPages)

        // todo: send newApp flag from location state to generate loginForm in login page by default
      }

      // set overlays
      if (appClient.overlaysMap?.size) {
        const overlayList = Array.from(appClient.overlaysMap.values()).map((overlay) => overlay.settings)
        setOverlays(fromSdkOverlayList(overlayList))
      }

      setAppBuilderLoaded(true)

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleUpdateData = useCallback((type: SubscriberUpdateType, appClient: IAppClient) => {
      switch (type) {
        case 'project': {
          setActiveOverlays((currentOverlays) => {
            if (currentOverlays.length) {
              const temp = [...currentOverlays]
              // TODO Find a better way to handle this, when switching between dark to light mode, the overlays behave weirdly and render outside apppreview
              setTimeout(() => {
                setActiveOverlays(temp)
              }, 100)
              return []
            }
            return currentOverlays
          })

          let projectAdapter = fromSdkApp(appClient.projectSettings!, projectPlanColor)
          setProject(projectAdapter)
          if (projectAdapter.statusBarColor) {
            setStatusBarColor({
              dark: isColorVariant(projectAdapter.statusBarColorDark) ? projectAdapter.statusBarColorDark : 'neutral-0',
              light: isColorVariant(projectAdapter.statusBarColor) ? projectAdapter.statusBarColor : 'neutral-0',
            })
          }
          if (projectAdapter.topBarColor) {
            setTopBarColor({
              light: isColorVariant(projectAdapter.topBarColor) ? projectAdapter.topBarColor : 'primary-90',
              dark: isColorVariant(projectAdapter.topBarColorDark) ? projectAdapter.topBarColorDark : 'primary-90',
            })
          }

          setShowBuiltInBranding(appClient.projectSettings?._WhiteLabeling || false)
          setShowInstallationModal(appClient.projectSettings?._InstallationModal || false)
          setShowCookieConfirmation(appClient.projectSettings?._CookiesNotification || false)

          break
        }
        case 'globals': {
          if (!appClient.builderSettings) break
          setBuilderGlobals({
            elements: fromSdkPageBody(appClient.builderSettings._Globals._Body, DEFAULT_COMPONENTS_GROUPS),
            settings: appClient.builderSettings._Globals._BodySettings || {},
          })
          break
        }
        case 'page': {
          const pageList = Array.from(appClient.pagesMap.values()).map((page) => page.settings)

          const pages = fromSdkPageList(pageList, DEFAULT_COMPONENTS_GROUPS)
          setPages(pages)

          // also update active detail overlay if currently exsited
          setActiveOverlays((prev) => {
            if (prev.length) {
              prev.forEach((activeOverlay, idx) => {
                let currentDetailPage = appClient.pagesMap.get(activeOverlay.id)?.settings
                if (!!currentDetailPage) {
                  const page = fromSdkPage(currentDetailPage, DEFAULT_COMPONENTS_GROUPS)
                  prev[idx] = {
                    ...prev[idx],
                    ...page,
                  } as any
                }
              })
            }

            return prev
          })

          break
        }
        case 'overlay': {
          const overlayList = Array.from(appClient.overlaysMap.values()).map((overlay) => overlay.settings)
          setOverlays(fromSdkOverlayList(overlayList))
          break
        }
        case 'dialog': {
          setDialogs(fromSdkDialogList(appClient.builderSettings?._Dialogs ?? []))
          break
        }
        case 'menu': {
          setMenus(fromSdkMenuList(appClient.builderSettings?._Menus ?? []))
          break
        }
        case 'navbar': {
          if (appClient.builderSettings && !isEmpty(appClient.builderSettings._NavBar)) {
            setNavbarSettings(fromSdkNavbarSettings(appClient.builderSettings._NavBar))
          }
          break
        }
      }
    }, [])

    const handleReloadBuilder = useCallback(async () => {
      setIsLoading(true)

      // clear all queries with compTableData cache
      queryClient.removeQueries({ queryKey: [queryKeys.compTableData] })

      // reset states
      setProject(undefined)
      setPages([])
      setOverlays([])
      setActiveOverlays([])
      setNavbarSettings(DEFAULT_NAV_SETTINGS)
      setMenus([])
      setDialogs([])

      // reload data
      await queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appID] })

      await appClient.PullBuilderSettings(appID)

      // reload data
      handleLoadData(appClient)

      // revalidate vesrion
      await queryClient.invalidateQueries({ queryKey: [queryKeys.appVersionHistory, appID] })

      setIsLoading(false)
    }, [appClient, appID, handleLoadData, queryClient])

    // Load AppClient on first render
    useEffect(() => {
      if (!appClient) return

      handleLoadData(appClient)

      // Subscribe to the active table
      // note: for rows, will have to get the data without fetch, since most mutation/changes
      // are optimistics updates, we cant wait for async operation to finish.
      const unsubscribe = appClient.subscribe((type) => {
        handleUpdateData(type, appClient)
      })

      // listen to force-reload event
      const handleDbClientReload = () => handleReloadBuilder()
      window.addEventListener(EVENT_KEYS.APP_BUILDER_RELOAD, handleDbClientReload)

      // Cleanup function to close database connection
      return () => {
        unsubscribe()
        window.removeEventListener(EVENT_KEYS.APP_BUILDER_RELOAD, handleDbClientReload)
      }
    }, [appClient, handleLoadData, handleReloadBuilder, handleUpdateData])

    /********************************************************
     * Builder
     ********************************************************/
    const [pageAction, setPageAction] = useState<PageAction | undefined>(undefined)
    const [activeTab, setActiveTab] = useState(LeadingSidebarTab.Page)
    const [activeComponent, setActiveComponent] = useState<ActiveComponentState | undefined>(undefined)
    const [activeLayer, setActiveLayer] = useState<ActiveLayerState>({
      type: 'page',
      id: '',
      index: -1,
      tableId: undefined,
    })
    const [customFonts, setCustomFonts] = useState<CustomFontsRecord>({})
    const [deviceScale, setDeviceScale] = useState(0.78)
    const [isFitScreen, setIsFitScreen] = useLocalStorage('BUILDER_FIT_SCREEN', true, {
      raw: false,
      serializer: (value) => (value ? '1' : '0'),
      deserializer: (value) => value === '1',
    })

    const [placeholderParentId, setPlaceholderParentId] = useState<string | null>(null)
    const [openMenu, setOpenMenu] = useState(false)
    const [canDnd, setCanDnd] = useState(true)
    const [listChecked, setListChecked] = useState<ListCheckState | undefined>(undefined)
    const [galleryModal, setGalleryModal] = useState<null | GalleryModal>(null)

    const previewFrameRef = useRef<HTMLIFrameElement>(null)

    /**
     * add component to designated layer (page/overlay)
     */
    const addBuilderComponent = useCallback(
      async (
        activeLayerClient: IAppLayer | undefined,
        component: IBody,
        elementSettings: Record<string, any>,
        childElementSettings: Record<string, Record<string, any>>,
        parentId?: string
      ) => {
        if (!activeLayerClient) return

        const targetParentId = placeholderParentId ?? parentId

        const parentBody = targetParentId
          ? activeLayerClient.componentsMap.get(targetParentId)?._Body || []
          : activeLayerClient.settings._Body

        const isFormElement = ['form', 'upload'].includes(component._GroupID)

        // form component, append to second last position in body, (before submit button)
        const position = isFormElement ? -1 : parentBody.length

        const flatSettings = {
          [component._id]: elementSettings,
          ...childElementSettings,
        }

        await activeLayerClient.AddComponent(component, flatSettings, targetParentId, position)

        if (placeholderParentId) {
          setPlaceholderParentId(null)
        }
      },
      [placeholderParentId, setPlaceholderParentId]
    )

    /********************************************************
     * Projects
     ********************************************************/
    const updateProjectStatusBarColor = useCallback(
      async (projectId: string, color: string, colorDark: string) => {
        try {
          await appClient.UpdateProjectStatusBarColor(projectId, color, colorDark)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }
      },
      [appClient, printAPIError]
    )

    const updateProjectNavBarColor = useCallback(
      async (projectId: string, color: string, colorDark: string) => {
        try {
          await appClient.UpdateProjectNavBarColor(projectId, color, colorDark)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }
      },
      [appClient, printAPIError]
    )

    const updateProjectTextColor = useCallback(
      async (projectId: string, color: string) => {
        try {
          await appClient.UpdateProjectTextColor(color)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }
      },
      [appClient, printAPIError]
    )

    const updateProjectNavBarVisibility = useCallback(
      async (visibility: boolean, iconVisibility?: boolean, titleVisibility?: boolean) => {
        try {
          await appClient.UpdateProjectNavBarVisibility(visibility, iconVisibility, titleVisibility)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }
      },
      [appClient, printAPIError]
    )

    const updateProjectSwitchers = useCallback(
      async (showLanguageSwitcher?: boolean, showThemeSwitcher?: boolean) => {
        try {
          await appClient.UpdateProjectSwitchers(showLanguageSwitcher, showThemeSwitcher)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }
      },
      [appClient, printAPIError]
    )

    const uploadAppIcon = useCallback(
      async (projectId: string, file: string | File | null) => {
        try {
          await appClient.UploadAppIcon(projectId, file)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }
      },
      [appClient, printAPIError]
    )

    const updateProjectPrivacy = useCallback(
      async (payload: IUpdateProjectPrivacyBody) => {
        // optimistic update
        let projectPrivacy = project?.privacy
        if (projectPrivacy) {
          projectPrivacy = {
            _PublicAcces: payload._PublicAcces,
            _PostAuthenticatePageID: payload._PostAuthenticatePageID,
            _PostLogoutPageID: payload._PostLogoutPageID,
            _PostUnauthenticatedPageID: payload._PostUnauthenticatedPageID,
            _AuthPlugin: {
              ...projectPrivacy?._AuthPlugin,
              _PluginID: payload._PluginID,
              _Working: payload._PublicAcces === false,
            },
            _UsersTableID: payload._UsersTableID,
            _EmailColID: payload._EmailColID,
            _NameColID: payload._NameColID,
            _PictureColID: payload._PictureColID,
            _RoleColID: payload._RoleColID,
            _PhoneColID: payload._PhoneColID,
          }
          setProject((prev) => ({
            ...prev!,
            privacy: projectPrivacy!,
          }))
        }

        await appClient.UpdateProjectPrivacy(payload)
      },
      [appClient, project?.privacy]
    )

    /********************************************************
     * NAVBAR SETTINGS
     ********************************************************/
    const updateNavbarSettings = useCallback(
      (setting: NavbarSettings) => {
        const viewport = setting.viewport

        let updatedSettings = {
          ...navbarSettings,
          [viewport]: setting,
        }
        appClient.UpdateNavbarSettings(toSdkNavbarSettings(updatedSettings))
      },
      [appClient, navbarSettings]
    )

    const updateAllNavbarSettings = useCallback(
      (settings: any) => {
        appClient.UpdateNavbarSettings(toSdkNavbarSettings(settings))
      },
      [appClient]
    )

    /********************************************************
     * MENUS
     ********************************************************/
    const [isMenuLoading, setIsMenuLoading] = useState(false)
    const [menuAction, setMenuAction] = useState<MenuAction | undefined>(undefined)
    const [menuItemAction, setMenuItemAction] = useState<
      | {
          flag: ActionFlag
          menuItemId: string
        }
      | undefined
    >(undefined)

    /** @undoable */
    const addMenu = useCallback(
      async (newMenu: BuilderMenu) => {
        setIsMenuLoading(true)
        try {
          await appClient.AddMenu(toSdkMenu(newMenu))
        } catch (e) {
          printAPIError(e as ErrorResponse)
        } finally {
          setIsMenuLoading(false)
        }

        updateVersion()

        return newMenu
      },
      [appClient, printAPIError, updateVersion]
    )

    /** @undoable */
    const deleteMenu = useCallback(
      async (id: string) => {
        try {
          await appClient.DeleteMenu(id)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }
        updateVersion()
      },
      [appClient, printAPIError, updateVersion]
    )

    /** @undoable */
    const updateMenu = useCallback(
      async (idx: number, cb: (prev: BuilderMenu) => void) => {
        let copy = [...menus]
        cb(copy[idx])
        setMenus(copy)

        const payload = toSdkMenu(copy[idx])

        try {
          await appClient.UpdateMenu(payload._id, payload)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }

        updateVersion()
      },
      [appClient, menus, printAPIError, updateVersion]
    )

    const deleteMenuItem = useCallback(
      async (menuIdx: number, itemId: string) => {
        await updateMenu(menuIdx, (prev) => {
          prev.items = prev.items.filter((item) => item.id !== itemId)
        })

        await updateVersion()
      },
      [updateMenu, updateVersion]
    )

    /********************************************************
     * DIALOGS
     ********************************************************/
    const [isDialogLoading, setIsDialogLoading] = useState(false)
    const [dialogAction, setDialogAction] = useState<DialogAction | undefined>(undefined)

    /** @undoable */
    const addDialog = useCallback(async () => {
      if (!appClient) return

      const newDialog: BuilderDialog = {
        id: uuid4(),
        name: genIdent(dialogs as unknown as Record<string, unknown>[], 'title', dialogs.length + 1, 'Dialog '),
        icon: 'info',
        showIcon: true,
        content: 'Please confirm to proceed',
        headline: 'Are you sure?',
        showDivider: false,
        showLeading: true,
        showTrailing: true,
        leadingLabel: 'Cancel',
        leadingActions: [
          {
            id: 'leading-actions-default',
            type: IActionType.CloseAlert,
            settings: {},
          },
        ],
        trailingLabel: 'Confirm',
        trailingActions: [
          {
            id: 'trailing-actions-default',
            type: IActionType.PerformNextAction,
            settings: {},
          },
        ],
        // todo: add alignment
        alignment: {
          titleAlignment: 'center',
          contentAlignment: 'center',
          footerAlignment: 'center',
          iconAlignment: 'center',
        },
        headlineTypography: 'Title Extra Small',
        contentTypography: 'Body Large',
        leadingButtonColor: 'neutral-100',
        trailingButtonColor: 'primary-50',
      }

      setIsDialogLoading(true)
      try {
        await appClient.AddDialog(toSdkDialog(newDialog))
      } catch (e) {
        printAPIError(e as ErrorResponse)
      } finally {
        setIsDialogLoading(false)
      }

      updateVersion()

      return newDialog
    }, [appClient, dialogs, printAPIError, updateVersion])

    /** @undoable */
    const deleteDialog = useCallback(
      async (id: string) => {
        if (!appClient) return

        try {
          await appClient.DeleteDialog(id)
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }

        updateVersion()
      },
      [appClient, printAPIError, updateVersion]
    )

    const updateDialog = useCallback(
      async (idx: number, cb: (prev: BuilderDialog) => void) => {
        let copy = [...dialogs]
        cb(copy[idx])
        setDialogs(copy)

        let payload = toSdkDialog(copy[idx])

        try {
          const res = await AppBuilderAPI.ReqUpdateDialog(appID, copy[idx].id, payload)

          if (appClient.builderSettings?._Dialogs) {
            appClient.builderSettings._Dialogs[idx] = res
          }
        } catch (e) {
          printAPIError(e as ErrorResponse)
        }

        await updateVersion()
      },
      [dialogs, updateVersion, appID, appClient.builderSettings?._Dialogs, printAPIError]
    )

    // ============================== Effects ==============================

    /**
     * Create some default components on newApp creation
     */
    useEffect(() => {
      if (!appClient) return

      const handleNewAppCreation = async () => {
        const isNewApp = window.sessionStorage.getItem('newAppId') === appClient.appID
        if (!isNewApp) return

        // find login page
        const loginPageClient = Array.from(appClient.pagesMap.values()).find(
          (p) => p.settings._Settings._Type === PageType.Login
        )
        if (!loginPageClient) return

        // create login component
        const [loginEl, loginSettings] = createElementRaw('login-form', {
          tables,
          menus,
          layer: loginPageClient,
        })

        if (!loginEl) return

        window.prettyLog('[App Builder] - created default login component', { loginEl, loginSettings })

        await addBuilderComponent(loginPageClient, loginEl, loginSettings, {})

        // remove the new app id from session storage
        window.sessionStorage.removeItem('newAppId')
      }

      handleNewAppCreation()
      //
    }, [addBuilderComponent, appClient, menus, tables])

    const openComponentSettings = useCallback(
      (component: IViewComponent, tab?: ComponentPanelTab) => {
        setActiveTab(LeadingSidebarTab.Component)
        if (tab) {
          setActiveComponentTab(tab)
        }
        setActiveComponent({
          layerType: activeLayer.type,
          compId: component._ComponentID || component._GroupID,
          id: component._id,
          groupId: component._GroupID,
        })
      },
      [activeLayer, setActiveComponent, setActiveTab]
    )

    const handleUpdateShowHideInfos = useCallback(
      async (id: string, settings: IUpdateProjectShowHideInfosBody) => {
        try {
          setShowBuiltInBranding(settings._WhiteLabeling || false)
          setShowInstallationModal(settings._InstallationModal || false)
          setShowCookieConfirmation(settings._CookiesNotification || false)
          await ProjectInfosAPI.ReqUpdateProjectShowHideInfos(id, settings)
          await queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, id] })
        } catch (error) {
          printAPIError(error as ErrorResponse)
        }
      },
      [printAPIError, queryClient]
    )

    /**
     * Format components to AI JSON
     */
    const formatComponentsToAiJSON = useCallback(
      async (id: string, layerId: string, layerType: 'page' | 'overlay') => {
        if (!appClient) return

        const layerClient = layerType === 'page' ? appClient.pagesMap.get(layerId) : appClient.overlaysMap.get(layerId)
        if (!layerClient) {
          return
        }
        const layerSettings = layerClient.settings._BodySettings || {}

        // collect body and body settings (from nested children if any)
        const comp = layerClient.componentsMap.get(id)
        if (!comp) {
          return
        }
        let body = comp.toBody()
        let bodySettings: Record<string, any> = {
          [id]: { ...layerSettings[id] },
        }

        function collectSettings(bodies: IBody[]) {
          for (const body of bodies) {
            bodySettings[body._id] = layerSettings[body._id]
            if (body._Body && body._Body.length > 0) {
              collectSettings(body._Body)
            }
          }
        }
        collectSettings(body._Body || [])

        const componentsAiInput = formatComponents([body], bodySettings)
        return componentsAiInput
      },
      [appClient]
    )

    /**
     * Export to AI JSON
     */
    const exportToAiJSON = useCallback(
      async (type: 'components' | 'page' | 'overlay' | 'dialog' | 'menu', data: Record<string, any>) => {
        if (!appClient) return

        let jsonOutput: string | undefined
        let jsonFileName: string | undefined

        try {
          switch (type) {
            case 'components':
              const json = await formatComponentsToAiJSON(data.id, data.layerId, data.layerType)
              if (json) {
                jsonOutput = JSON.stringify(json, null, 2)
                jsonFileName = `component-export`
              }
              break
            case 'page':
              const pageId = data.id as string
              const pageClient = appClient.pagesMap.get(pageId)
              if (!pageClient) {
                break
              }
              const pageAiInput = formatAiPage(pageClient.settings)
              // download to json
              jsonOutput = JSON.stringify(pageAiInput, null, 2)
              jsonFileName = `page-export`
              break
            case 'overlay':
              const overlayId = data.id as string
              const overlayClient = appClient.overlaysMap.get(overlayId)
              if (!overlayClient) {
                break
              }
              const overlayAiInput = formatAiOverlay(overlayClient.settings)
              // download to json
              jsonOutput = JSON.stringify(overlayAiInput, null, 2)
              jsonFileName = `overlay-export`
              break
            case 'dialog':
              const dialogId = data.id as string
              const dialogClient = appClient.builderSettings?._Dialogs.find((d) => d._id === dialogId)
              if (!dialogClient) {
                break
              }
              const dialogAiInput = formatAiDialog(dialogClient)
              // download to json
              jsonOutput = JSON.stringify(dialogAiInput, null, 2)
              jsonFileName = `dialog-export`
              break
            case 'menu':
              const menuId = data.id as string
              const menuClient = appClient.builderSettings?._Menus.find((m) => m._id === menuId)
              if (!menuClient) {
                break
              }
              const menuAiInput = formatAiMenu(menuClient)
              // download to json
              jsonOutput = JSON.stringify(menuAiInput, null, 2)
              jsonFileName = `menu-export`
              break
          }
        } catch (err) {
          printAPIError(err as ErrorResponse)
        }

        // download the json file
        if (jsonOutput && jsonFileName) {
          const blob = new Blob([jsonOutput], { type: 'application/json' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `${jsonFileName}.json`
          a.click()
          URL.revokeObjectURL(url)
        }
      },
      [appClient, formatComponentsToAiJSON, printAPIError]
    )

    /**
     * Copy components to AI JSON
     */
    const copyComponentsToAiJSON = useCallback(
      async (id: string, layerId: string, layerType: 'page' | 'overlay') => {
        const output = await formatComponentsToAiJSON(id, layerId, layerType)
        if (output) {
          navigator.clipboard.writeText(JSON.stringify(output, null, 2))
        }
      },
      [formatComponentsToAiJSON]
    )

    return {
      exportToAiJSON,
      copyComponentsToAiJSON,

      // app client
      appID,
      appUserTableID,
      appUserTable,
      appClient,
      project,
      version,
      setVersion,
      updateVersion,

      // project
      updateProjectNavBarColor,
      updateProjectStatusBarColor,
      uploadAppIcon,
      updateProjectPrivacy,
      updateProjectTextColor,
      updateProjectNavBarVisibility,
      updateProjectSwitchers,

      // builder
      appBuilderLoaded,
      activeLayer,
      setActiveLayer,
      activeComponent,
      setActiveComponent,
      deviceScale,
      setDeviceScale,
      isFitScreen,
      setIsFitScreen,
      topBarColor,
      setTopBarColor,
      setStatusBarColor,
      statusBarColor,
      previewFrameRef,
      customFonts,
      setCustomFonts,
      toolbar,
      setToolbar,
      userTable,
      isLoading,
      canDnd,
      setCanDnd,
      listChecked,
      setListChecked,
      galleryModal,
      setGalleryModal,
      openMenu,
      setOpenMenu,
      topBarVisibility,
      setTopBarVisibility,
      topBarShowIcon,
      setTopBarShowIcon,
      topBarShowTitle,
      showLanguageSwitcher,
      setShowLanguageSwitcher,
      showThemeSwitcher,
      setShowThemeSwitcher,
      setTopBarShowTitle,
      placeholderParentId,
      setPlaceholderParentId,
      addBuilderComponent,

      // globals
      builderGlobals,
      setBuilderGlobals,

      // menus
      menus,
      setMenus,
      isMenuLoading,
      setIsMenuLoading,
      menuAction,
      setMenuAction,
      menuItemAction,
      setMenuItemAction,
      addMenu,
      updateMenu,
      deleteMenu,
      deleteMenuItem,

      // dialogs
      dialogs,
      setDialogs,
      isDialogLoading,
      setIsDialogLoading,
      dialogAction,
      setDialogAction,
      addDialog,
      updateDialog,
      deleteDialog,

      // pages
      pages,
      setPages,
      userProfilePage,

      // overlays
      overlays,
      setOverlays,
      activeOverlays,
      setActiveOverlays,

      // tab, components, pageAction
      activeTab,
      setActiveTab,
      components: DEFAULT_COMPONENTS_GROUPS,
      pageAction,
      setPageAction,

      // methods
      handleLoadData,

      // navbar
      navbarSettings,
      setNavbarSettings,
      updateNavbarSettings,
      updateAllNavbarSettings,

      showFabWarningModal,
      setShowFabWarningModal,
      fabWarningReason,
      setFabWarningReason,
      showDateTimeValidationModal,
      setShowDateTimeValidationModal,

      // clipboard
      clipboard,
      setClipboard,
      clipboardAll,
      setClipboardAll,

      // development mode
      isDevelopmentMode,
      setIsDevelopmentMode,
      openComponentSettings,
      activeComponentAccordion,
      setActiveComponentAccordion,
      activeComponentTab,
      setActiveComponentTab,

      // branding
      showBuiltInBranding,
      setShowBuiltInBranding,
      showInstallationModal,
      setShowInstallationModal,
      showCookieConfirmation,
      setShowCookieConfirmation,
      handleUpdateShowHideInfos,
    }
  }
)

export { AppBuilderProvider, useAppBuilderContext }
